import { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { marked } from "marked";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/SeoHead";
import { POSTS, getPostByPath, readingTime } from "@/content/blog/posts.mjs";
import NotFound from "@/pages/NotFound";
import "@/styles/article.css";

const fmt = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });

export default function ArticlePage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const post = getPostByPath(pathname);

  const html = useMemo(() => (post ? marked.parse(post.body) : ""), [post]);

  if (!post) return <NotFound />;

  const related = (post.related || [])
    .map((slug) => POSTS.find((p) => p.slug === slug))
    .filter(Boolean);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: post.updated || post.date,
      author: { "@type": "Organization", name: "Sema", url: "https://semalayer.com" },
      publisher: { "@type": "Organization", name: "Sema" },
      mainEntityOfPage: `https://semalayer.com${post.path}`,
    },
  ];
  if (post.faq?.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  // Keep SPA navigation for internal links inside rendered markdown.
  const onArticleClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href");
    if (href?.startsWith("/") && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <div data-testid="article-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead
        title={post.seoTitle || post.title}
        description={post.description}
        keywords={post.keywords}
        path={post.path}
        image={post.ogImage || post.image}
        type="article"
        jsonLd={jsonLd}
      />
      <MarketingNav />

      <article className="relative pt-32 pb-16 sm:pt-36">
        <div className="mx-auto max-w-3xl px-6">
          <Link
            to="/blog"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Blog
          </Link>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-accent">{post.category}</p>
          <h1 data-testid="article-h1" className="mt-4 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {post.author || "Sema Team"} · {fmt(post.date)} · {readingTime(post)} min read
          </p>
          <div aria-hidden className="sema-accent-underline mt-8" />

          <img
            src={post.image}
            alt={post.title}
            data-testid="article-hero-image"
            className="mt-10 aspect-[3/2] w-full rounded-2xl border border-border/60 object-cover"
            loading="eager"
            fetchPriority="high"
          />

          <div
            data-testid="article-body"
            className="sema-article mt-10"
            onClick={onArticleClick}
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {post.faq?.length > 0 && (
            <section data-testid="article-faq" className="mt-14">
              <h2 className="text-2xl font-semibold tracking-tight">Frequently asked questions</h2>
              <div className="mt-6 space-y-4">
                {post.faq.map((f) => (
                  <details key={f.q} className="group rounded-xl border border-border/60 bg-card/40 p-5">
                    <summary className="cursor-pointer list-none font-medium leading-snug marker:hidden">
                      {f.q}
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <div
            data-testid="article-cta"
            className="mt-16 rounded-2xl border border-accent/40 bg-accent/5 p-8 text-center"
          >
            <h2 className="text-2xl font-semibold tracking-tight">See a governed semantic layer on your own data</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              Connect a source or upload a CSV, and ask your first plain-English question in minutes — every answer
              ships with its SQL.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/signup">
                <Button
                  data-testid="article-cta-signup"
                  className="rounded-full bg-accent px-6 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground hover:bg-accent/90"
                >
                  Start free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/demo">
                <Button variant="outline" className="rounded-full border-border/70 px-6 font-mono text-xs uppercase tracking-[0.2em]">
                  Book a walkthrough
                </Button>
              </Link>
            </div>
          </div>

          {related.length > 0 && (
            <section data-testid="article-related" className="mt-16">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Keep reading</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={r.path}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="group rounded-xl border border-border/60 bg-card/40 p-5 transition-colors hover:border-accent/50"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{r.category}</p>
                    <p className="mt-2 font-medium leading-snug group-hover:text-accent">{r.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}
